import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

// Bind the feature image to a distinct gallery photo (index 5) so the Preferred
// Employer intro doesn't repeat the same photo used by other intro sections.
const imageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[5].alt_text',
      defaultValue: 'Community lifestyle',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick one welcoming lifestyle photo — clubhouse, lounge, entryway, or a lifestyle interior shot that reads as premium and inviting. Never choose a photo that shows a toilet.',
    context:
      'Single feature image next to the Preferred Employer intro copy — sets the tone as premium, welcoming, and community-oriented.',
    sourceCollection: 'photo_gallery',
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Preferred Employer page intro: a two-column stripe below the hero. Left
 * column carries a headline and a welcoming blurb explaining the program;
 * right column shows a lifestyle feature photo. Mirrors the intro-first
 * pattern used by AmenitiesIntro on the amenities page.
 */
export function PreferredEmployerIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const image = useSlotGroup('preferredemployerintro.image', imageDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] py-12 pb-6 md:py-20"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <RichTextContent
              path="preferredemployerintro.heading"
              label="Heading"
              defaultValue='<p>A Welcome Home For Local Employers</p>'
              as="h2"
              className="text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.05] mb-6"
              style={{ color: '#000', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
            />

            <RichTextContent
              path="preferredemployerintro.body"
              label="Body Text"
              defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, we believe your work should be rewarded — even at home. Our Preferred Employer Program partners with local businesses, hospitals, schools, and organizations to offer their teams exclusive move-in savings, streamlined leasing, and a warm welcome to our community. If your employer is on our preferred list, you qualify for benefits designed to make your transition seamless.</p>'
              as="div"
              className="font-body text-base leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
            />
          </div>

          {/* Right: feature image */}
          <EditableSlotGroup groupPath="preferredemployerintro.image" definition={imageDef}>
            <div className="pei-image-wrap">
              {image.image ? (
                <VibesitesImage
                  url={image.image}
                  alt={image.alt || 'Community lifestyle'}
                  className="pei-image"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              ) : (
                <div className="pei-image pei-image--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Feature image fills its column with a stable landscape ratio. */
        .pei-image-wrap {
          width: 100%;
          overflow: hidden;
          border-radius: 0;
        }
        .pei-image {
          display: block;
          width: 100%;
          aspect-ratio: 4 / 3;
          object-fit: cover;
          object-position: center;
        }
        .pei-image--placeholder {
          background: var(--vibe-color-surface);
        }

        @media (max-width: 768px) {
          .pei-image {
            aspect-ratio: 16 / 10;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerIntro',
  displayName: 'Preferred Employer Intro',
  tokens: {
    'preferredemployerintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>A Welcome Home For Local Employers</p>',
    },
    'preferredemployerintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we believe your work should be rewarded — even at home. Our Preferred Employer Program partners with local businesses, hospitals, schools, and organizations to offer their teams exclusive move-in savings, streamlined leasing, and a warm welcome to our community. If your employer is on our preferred list, you qualify for benefits designed to make your transition seamless.</p>',
    },
  },
  slotGroups: {
    'preferredemployerintro.image': imageDef,
  },
};

(PreferredEmployerIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerIntro;
